if (!customElements.get('my-flipbook')) {
	customElements.define(
		'my-flipbook',
		class Flipbook extends HTMLElement {
			async connectedCallback() {
				this.items = this.querySelectorAll('.flipbook-item');
				if (!this.items.length) return;

				this.items.forEach(item => {
					if (item.classList.contains('active')) {
						this.initFlipbookItem(item);
					}
				});

				this.initTabs();
			}

			createFlipbookPage({ width, height, imgSrc, imgAlt = '', pageClass = '', imageClass = '' }) {
				const page = document.createElement('div');
				page.className = 'flipbook-page';
				page.style.width = `${width}px`;
				page.style.height = `${height}px`;

				const img = new Image();
				img.src = imgSrc;
				img.alt = imgAlt;
				img.style.width = '100%';
				img.style.height = '100%';

				const pageImage = document.createElement('div');
				pageImage.className = `page-image ${imageClass}`.trim();

				pageImage.appendChild(img);
				page.appendChild(pageImage);

				if (pageClass) {
					page.classList.add(pageClass);
				}

				return page;
			}

			async createPseudoPage(position, width, height) {
				const pseudo = document.createElement('div');
				pseudo.className = 'flipbook-page pseudo-page';
				pseudo.style.background = 'white';
				pseudo.style.width = `${width}px`;
				pseudo.style.height = `${height}px`;

				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				ctx.fillStyle = 'white';
				ctx.fillRect(0, 0, canvas.width, canvas.height);

				const blobUrl = await this.canvasToBlobURL(canvas, 'image/png');

				const img = new Image();
				img.src = blobUrl;
				img.alt = `Blank ${position} page`;
				img.style.width = '100%';
				img.style.height = '100%';

				const pageImage = document.createElement('div');
				pageImage.className = `page-image ${position}`;
				pageImage.appendChild(img);

				pseudo.appendChild(pageImage);
				return pseudo;
			}

			async renderPageImage(page, scale) {
				const viewport = page.getViewport({ scale });
				const canvas = document.createElement('canvas');
				canvas.width = viewport.width;
				canvas.height = viewport.height;

				const context = canvas.getContext('2d');
				await page.render({ canvasContext: context, viewport }).promise;

				return { canvas, viewport };
			}

			canvasToBlobURL(canvas, type = 'image/png', quality = 1.0) {
				return new Promise((resolve) => {
					canvas.toBlob((blob) => {
						if (!blob) {
							resolve(null);
							return;
						}
						const url = URL.createObjectURL(blob);
						resolve(url);
					}, type, quality);
				});
			}

			async initFlipbookItem(item) {
				item.dataset.initialized = 'true';
				const loader =  item.querySelector('.flipbook-loader');
				const output = item.querySelector('.flipbook-pages');
				const overviewItems = item.querySelectorAll('.flipbook-overview-item');
				const overviewWrapper = item.querySelector('.flipbook-overview-wrapper');
				const tabs = this.querySelector('.flipbook-header');
				tabs?.classList.add('disabled');
				const pdfUrl = item.dataset.pdf;
				if (!pdfUrl || !output) return;

				try {
					const loadingTask = pdfjsLib.getDocument(pdfUrl);
					const pdf = await loadingTask.promise;
					loader.style.display = 'block';

					const scale = 2.0;
					const firstPage = await pdf.getPage(1);
					const lastPage = await pdf.getPage(pdf.numPages);
					const firstViewport = firstPage.getViewport({ scale });
					const lastViewport = lastPage.getViewport({ scale });

					const firstIsVertical = firstViewport.height > firstViewport.width;
					const lastIsVertical = lastViewport.height > lastViewport.width;

					output.innerHTML = '';

					if (firstIsVertical) {
						output.appendChild(await this.createPseudoPage('start', firstViewport.width, firstViewport.height));
					}

					// Місце для запам'ятовування першої горизонтальної сторінки
					let horizontalViewport = null;

					for (let i = 1; i <= pdf.numPages; i++) {
						const page = await pdf.getPage(i);
						const { canvas, viewport } = await this.renderPageImage(page, scale);

						// шукаємо першу горизонтальну сторінку
						if (!horizontalViewport && viewport.width > viewport.height) {
							horizontalViewport = viewport;
						}

						if (i === 1 && firstIsVertical) {
							output.appendChild(this.createFlipbookPage({
								width: canvas.width,
								height: canvas.height,
								imgSrc: await this.canvasToBlobURL(canvas),
								imgAlt: `Page ${i}`,
								imageClass: 'main-vertical start-right'
							}));
							continue;
						}

						if (i === pdf.numPages && lastIsVertical) {
							output.appendChild(this.createFlipbookPage({
								width: canvas.width,
								height: canvas.height,
								imgSrc: await this.canvasToBlobURL(canvas),
								imgAlt: `Page ${i}`,
								imageClass: 'left'
							}));
							continue;
						}

						if (viewport.width > viewport.height) {
							// разделение на левую и правую половину
							const halfWidth = canvas.width / 2;

							const leftCanvas = document.createElement('canvas');
							leftCanvas.width = halfWidth;
							leftCanvas.height = canvas.height;
							leftCanvas.getContext('2d').drawImage(canvas, 0, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);

							const rightCanvas = document.createElement('canvas');
							rightCanvas.width = halfWidth;
							rightCanvas.height = canvas.height;
							rightCanvas.getContext('2d').drawImage(canvas, halfWidth, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);

							output.appendChild(this.createFlipbookPage({
								width: halfWidth,
								height: canvas.height,
								imgSrc: await this.canvasToBlobURL(leftCanvas),
								imageClass: i === 1 ? 'start' : 'left'
							}));

							output.appendChild(this.createFlipbookPage({
								width: halfWidth,
								height: canvas.height,
								imgSrc: await this.canvasToBlobURL(rightCanvas),
								imageClass:
									i === 1
									? 'start-right'
									: i === pdf.numPages
										? 'end last-right'
										: 'right'
							}));

						} else {
							output.appendChild(this.createFlipbookPage({
								width: canvas.width,
								height: canvas.height,
								imgSrc: await this.canvasToBlobURL(canvas),
								imageClass: 'vertical'
							}));
						}
					}

					if (lastIsVertical) {
						output.appendChild(await this.createPseudoPage('end last-right', lastViewport.width, lastViewport.height));
					}

					// Визначаємо розміри для PageFlip
					let w, h;
					if (horizontalViewport) {
						w = Math.floor(horizontalViewport.width);
						h = Math.floor(horizontalViewport.height);
					} else {
						w = firstIsVertical ? Math.floor(firstViewport.width * 2) : Math.floor(firstViewport.width);
						h = Math.floor(firstViewport.height);
					}

					const ratioPercent = (h / w) * 100;

					// item.querySelector('.flipbook-cover').style.aspectRatio = `${w} / ${h}`;
					// item.querySelector('.flipbook-pages').style.aspectRatio = `${w} / ${h}`;

					item.querySelector('.flipbook-cover').style.paddingTop = ratioPercent + '%';

					const startPage = overviewItems.length > 0 ? parseInt(overviewItems[0].dataset.page, 10): 1;
					const pageFlip = new St.PageFlip(output, {
						width: horizontalViewport ? Math.floor(horizontalViewport.width / 2) : (firstIsVertical ? Math.floor(firstViewport.width) : Math.floor(firstViewport.width) / 2),
						height: horizontalViewport ? Math.floor(horizontalViewport.height) : Math.floor(firstViewport.height),
						size: "stretch",
						mode: 'single',
						flippingTime: 700,
						startPage: startPage,
						maxShadowOpacity: 0.2,
						mobileScrollSupport: false
					});

					pageFlip.loadFromHTML(item.querySelectorAll('.flipbook-page'));
					loader.style.display = 'none';
					overviewWrapper?.classList.remove('disabled');
					tabs?.classList.remove('disabled');

					let currentPage = pageFlip.getCurrentPageIndex() + 1;

					item.querySelector(".btn-prev").classList.toggle("disabled", currentPage === 1);

					if (overviewItems.length > 0) {
						overviewItems.forEach((el) => {
							el.addEventListener('click', () => {
								const targetPage = parseInt(el.dataset.page, 10);
								if (el.classList.contains('active') && targetPage === currentPage) return;
								if (!isNaN(targetPage) && targetPage <= pageFlip.getPageCount()) {
									pageFlip.flip(targetPage);
									currentPage = targetPage;
									this.updateActiveDots(currentPage, overviewItems);
								}
							});
						});
					}

					pageFlip.on("flip", (e) => {
						currentPage = e.data + 1;
						overviewItems.length > 0 && this.updateActiveDots(currentPage, overviewItems);

						item.querySelector(".btn-prev").classList.toggle("disabled", currentPage === 1);
						item.querySelector(".btn-next").classList.toggle("disabled", currentPage >= pageFlip.getPageCount() - 1);
					});

					item.querySelector(".btn-prev").addEventListener("click", () => pageFlip.flipPrev());
					item.querySelector(".btn-next").addEventListener("click", () => pageFlip.flipNext());

				} catch (err) {
					output.innerHTML = 'Error ' + err.message;
					console.error(err);
				}
			}

			updateActiveDots(currentPageIndex, overviewItems) {
				overviewItems.forEach(el => {
					const dotPage = parseInt(el.dataset.page, 10);
					el.classList.toggle('active', dotPage <= currentPageIndex);
				});
			}

			initTabs() {
				const tabs = this.querySelectorAll('.flipbook-variant-tab__item');
				const items = this.querySelectorAll('.flipbook-item');

				tabs.forEach((tab, i) => {
					tab.addEventListener('click', async () => {
						tabs.forEach(t => t.classList.remove('active'));
						items.forEach(it => it.classList.remove('active'));

						tab.classList.add('active');
						items[i].classList.add('active');

						if (!items[i].dataset.initialized) {
							await this.initFlipbookItem(items[i]);
						}
					});
				});
			}
		}
	);
}